/**
 * JSON-schema → portal parameter-model mapping.
 *
 * These two helpers translate raw OpenAPI / x-ms-* annotations into the
 * portal's internal {@link ParameterType} / {@link ParameterEditor} enums.
 * The mapping is identical wherever it's used — historically duplicated
 * byte-for-byte across operationManifestService.ts and dynamicSchemaService.ts.
 *
 * Pure: no React, no stores, no services.
 */

import { ParameterEditor, ParameterType } from '@/types/operationParameter';

/**
 * Convert a JSON-schema `type` string into the portal's {@link ParameterType}.
 *
 * Unknown / missing types collapse to {@link ParameterType.Any}, matching
 * the historical fallback used by both manifest and dynamic-schema services.
 */
export function schemaTypeToParameterType(schemaType?: string): ParameterType {
  switch (schemaType) {
    case 'string':
      return ParameterType.String;
    case 'integer':
      return ParameterType.Integer;
    case 'number':
      return ParameterType.Number;
    case 'boolean':
      return ParameterType.Boolean;
    case 'array':
      return ParameterType.Array;
    case 'object':
      return ParameterType.Object;
    default:
      return ParameterType.Any;
  }
}

const XMS_EDITOR_MAP: Record<string, ParameterEditor> = {
  // Standard editors
  'string': ParameterEditor.String,
  'textarea': ParameterEditor.TextArea,
  'code': ParameterEditor.Code,
  'combobox': ParameterEditor.Combobox,
  'dropdown': ParameterEditor.Combobox, // dropdown is same as combobox
  'boolean': ParameterEditor.Boolean,
  'datetime': ParameterEditor.DateTime,
  'file': ParameterEditor.File,
  'dictionary': ParameterEditor.Dictionary,
  'array': ParameterEditor.Array,
  'condition': ParameterEditor.Condition,
  'authentication': ParameterEditor.Authentication,
  'schema': ParameterEditor.Schema,
  'table': ParameterEditor.Table,
  'recurrence': ParameterEditor.Recurrence,
  'copyable': ParameterEditor.CopyPaste,
  'copypaste': ParameterEditor.CopyPaste,
  // Custom editors that map to standard types
  'initializevariable': ParameterEditor.InitializeVariable, // Variable initialization editor
  'variablename': ParameterEditor.VariableName, // Dropdown of variables defined by InitializeVariable actions
  'agentinstruction': ParameterEditor.AgentInstruction, // Agent instructions editor (system + user messages)
  'workflowpicker': ParameterEditor.WorkflowPicker, // Picker of callable workflows in the same Logic App
  'readonlyexternal': ParameterEditor.ReadOnlyExternal, // Read-only display backed by an external system
};

/**
 * Translate an `x-ms-editor` annotation from a connector manifest into the
 * matching {@link ParameterEditor}.
 *
 * Returns `null` when the editor name is unrecognized so callers can fall
 * back to the inferred-editor pipeline.
 *
 * Includes:
 *   - Standard editors (string, textarea, code, combobox, …)
 *   - The `dropdown` alias for `combobox`
 *   - Otto-specific custom editors (initializevariable, variablename,
 *     agentinstruction, workflowpicker, readonlyexternal)
 */
export function mapXmsEditorToParameterEditor(xmsEditor: string): ParameterEditor | null {
  return XMS_EDITOR_MAP[xmsEditor.toLowerCase()] ?? null;
}
